/**
 * Tipagem dos eventos entregues pelo stream SSE `/v1/notifications/stream`.
 * Cada evento SSE usa um destes nomes no campo `event:` (case-sensitive) e o
 * payload JSON correspondente — devem casar 1:1 com o backend.
 */

export type LeadSource = "portal" | "meta" | "whatsapp" | "manual" | "referral";

export type ProposalStatus = "accepted" | "rejected" | "expired" | "countered";

export type ShareInvitationStatus = "accepted" | "rejected" | "expired";

export type PartnerInviteStatus = "accepted" | "declined";

export type ActivityType =
  | "visit"
  | "call"
  | "meeting"
  | "follow_up"
  | "inspection"
  | "signing"
  | "other";

export interface NewLeadPayload {
  id: string;
  personName: string;
  propertyId: string | null;
  source: LeadSource;
  createdAt: string;
}

export interface ProposalReceivedPayload {
  id: string;
  proposalGroupId: string;
  version: number;
  propertyId: string;
  personId: string;
  offerValue: number;
  createdAt: string;
}

export interface ProposalStatusChangedPayload {
  id: string;
  proposalGroupId: string;
  status: ProposalStatus;
  changedAt: string;
}

export interface ShareInvitationReceivedPayload {
  id: string;
  propertyId: string;
  ownerBrokerId: string;
  ownerBrokerName: string;
  commissionPercentage: number;
}

export interface ShareInvitationRespondedPayload {
  id: string;
  propertyId: string;
  status: ShareInvitationStatus;
  respondedAt: string;
}

export interface ShareRevokedPayload {
  propertyId: string;
  revokedAt: string;
}

export interface ActivityReminderPayload {
  id: string;
  activityType: ActivityType;
  scheduledAt: string;
  personName: string;
  propertyTitle: string;
}

export interface PaymentConfirmedPayload {
  subscriptionId: string;
  amount: number;
  paidAt: string;
}

/** O staff do suporte respondeu um chamado aberto pelo corretor (origem: backoffice). */
export interface SupportTicketRepliedPayload {
  ticketId: string;
  code: string;
  subject: string;
  messageId: string;
}

/** Laudo PDF da vistoria ficou pronto (worker assíncrono do crm-api, ADR-0034). */
export interface InspectionReportReadyPayload {
  inspectionId: string;
  propertyTitle: string;
}

/** Minuta PDF do contrato ficou pronta (worker assíncrono do crm-api, ADR-0034). */
export interface ContractMinutaReadyPayload {
  contractId: string;
  internalCode: string;
}

/** Convite de parceria recebido (rede de parceiros por convite, #379/#380). */
export interface PartnerInviteReceivedPayload {
  inviteId: string;
  inviterBrokerId: string;
  inviterName: string;
  inviterPublicCode?: string | null;
}

/** O convidado respondeu o convite de parceria. */
export interface PartnerInviteRespondedPayload {
  inviteId: string;
  status: PartnerInviteStatus;
  respondedAt: string;
}

export type AccessAuthorizationResponse = "authorized" | "denied";

/** Resposta do proprietário ao check-in periódico de disponibilidade do imóvel. */
export type AvailabilityAnswer = "available" | "closed_deal" | "paused";

/** O proprietário respondeu a autorização de acesso ao imóvel (página pública /p/acesso). */
export interface AccessAuthorizationRespondedPayload {
  id: string;
  propertyId: string;
  propertyTitle: string;
  status: AccessAuthorizationResponse;
  respondedAt: string;
}

/**
 * Um corretor parceiro pediu um imóvel via broadcast de demanda (hub#632).
 * Sem PII do cliente: só os critérios (operação/local) + observação opcional.
 * `requestId` referencia o pedido na caixa de entrada (`GET /v1/demand-requests`).
 */
export interface DemandBroadcastReceivedPayload {
  requestId: string;
  operation: string;
  city?: string | null;
  uf?: string | null;
  note?: string | null;
}

/**
 * O proprietário respondeu o check-in periódico de disponibilidade do imóvel
 * (página pública por token). `answer` mapeia o novo status do imóvel no backend.
 */
export interface PropertyAvailabilityRespondedPayload {
  checkId: string;
  propertyId: string;
  propertyTitle: string;
  answer: AvailabilityAnswer;
  message?: string | null;
}

/**
 * O proprietário decidiu o que fazer com o imóvel depois que o contrato encerrou
 * (página pública por token, hub#760). `source` distingue a resposta do próprio dono
 * (`owner_page`) da que o corretor registrou por fora (`broker`) e do lembrete da data
 * prevista de preparo (`reminder`).
 */
export interface PropertyReturnRespondedPayload {
  checkId: string;
  propertyId: string;
  propertyTitle: string;
  answer: "relist" | "repairs_first" | "pause" | "withdraw";
  source: "owner_page" | "broker" | "reminder";
  expectedAvailableOn?: string | null;
  message?: string | null;
}

/**
 * O proprietário abriu, pela primeira vez, a carta de boas-vindas do imóvel
 * (página pública por token). `channel` diz de onde veio o clique. hub#707.
 */
export interface OwnerWelcomeViewedPayload {
  welcomeId: string;
  propertyId: string;
  propertyTitle: string;
  ownerName?: string | null;
  /** email | whatsapp | direct */
  channel: string;
}

/**
 * Uma construtora convidou o corretor para um lançamento (Rede de Vendas, hub#753).
 * <p>
 * `adoptionId` é o id do convite no domínio de launches — é por ele que o corretor
 * aceita ou recusa. `launchId` é nulo quando o convite é de PARCERIA (entrar na rede
 * da construtora) em vez de um lançamento específico: são dois convites diferentes
 * que chegam pela mesma notificação.
 */
export interface LaunchInviteReceivedPayload {
  adoptionId: string;
  launchId?: string | null;
  launchName: string;
  developerName: string;
  invitedAt: string;
}

export type NotificationEvent =
  | { type: "NewLead"; payload: NewLeadPayload }
  | { type: "ProposalReceived"; payload: ProposalReceivedPayload }
  | { type: "ProposalStatusChanged"; payload: ProposalStatusChangedPayload }
  | { type: "ShareInvitationReceived"; payload: ShareInvitationReceivedPayload }
  | { type: "ShareInvitationResponded"; payload: ShareInvitationRespondedPayload }
  | { type: "ShareRevoked"; payload: ShareRevokedPayload }
  | { type: "ActivityReminder"; payload: ActivityReminderPayload }
  | { type: "PaymentConfirmed"; payload: PaymentConfirmedPayload }
  | { type: "SupportTicketReplied"; payload: SupportTicketRepliedPayload }
  | { type: "InspectionReportReady"; payload: InspectionReportReadyPayload }
  | { type: "ContractMinutaReady"; payload: ContractMinutaReadyPayload }
  | { type: "PartnerInviteReceived"; payload: PartnerInviteReceivedPayload }
  | { type: "PartnerInviteResponded"; payload: PartnerInviteRespondedPayload }
  | { type: "AccessAuthorizationResponded"; payload: AccessAuthorizationRespondedPayload }
  | { type: "PropertyAvailabilityResponded"; payload: PropertyAvailabilityRespondedPayload }
  | { type: "PropertyReturnResponded"; payload: PropertyReturnRespondedPayload }
  | { type: "OwnerWelcomeViewed"; payload: OwnerWelcomeViewedPayload }
  | { type: "DemandBroadcastReceived"; payload: DemandBroadcastReceivedPayload }
  | { type: "LaunchInviteReceived"; payload: LaunchInviteReceivedPayload };

export type NotificationType = NotificationEvent["type"];

export const NOTIFICATION_TYPES: NotificationType[] = [
  "NewLead",
  "ProposalReceived",
  "ProposalStatusChanged",
  "ShareInvitationReceived",
  "ShareInvitationResponded",
  "ShareRevoked",
  "ActivityReminder",
  "PaymentConfirmed",
  "SupportTicketReplied",
  "InspectionReportReady",
  "ContractMinutaReady",
  "PartnerInviteReceived",
  "PartnerInviteResponded",
  "AccessAuthorizationResponded",
  "PropertyAvailabilityResponded",
  "PropertyReturnResponded",
  "OwnerWelcomeViewed",
  "DemandBroadcastReceived",
  "LaunchInviteReceived",
];

export type ConnectionStatus = "connected" | "reconnecting" | "disconnected";

/**
 * Sinal SLIM entregue pelo evento SSE `notification` do serviço central
 * (contrato "option C", ADR-0025): apenas uma dica, NÃO o `NotificationDto`
 * completo. `id` é o UUID/cursor do servidor, `type` o `NotificationType` e
 * `unread` a contagem atual de não lidas. O conteúdo da lista continua vindo do
 * REST (`GET /notifications`); o sinal só dispara reconciliação + toast genérico.
 */
export interface NotificationSignal {
  id: string;
  type: NotificationType;
  unread: number;
}

/** Variante por tipo: garante que `n.type === 'X'` restringe `n.payload`. */
type StoredVariant<T extends NotificationType> = {
  /** UUID local — o backend não devolve um id próprio para a notificação,
   *  apenas o id do recurso referenciado (lead, proposta, etc). */
  id: string;
  type: T;
  payload: Extract<NotificationEvent, { type: T }>["payload"];
  receivedAt: string;
  read: boolean;
};

export type StoredNotification = {
  [K in NotificationType]: StoredVariant<K>;
}[NotificationType];

/**
 * Notificação persistida vinda do servidor — entregue tanto pela lista REST
 * (`GET /v1/notifications`) quanto pelo push SSE (`data:` do stream). O `id` é o
 * do servidor; o front faz upsert por ele (dedup entre REST e SSE).
 */
export type NotificationDto = {
  [K in NotificationType]: {
    id: string;
    type: K;
    payload: Extract<NotificationEvent, { type: K }>["payload"];
    read: boolean;
    readAt: string | null;
    createdAt: string;
  };
}[NotificationType];

/** Página da lista de notificações + total de não lidas (badge do sino). */
export interface NotificationPage {
  items: NotificationDto[];
  nextCursor: string | null;
  hasMore: boolean;
  unreadCount: number;
}

/** Mensagens pt-BR dos toasts (lado do front; back só emite o dado bruto). */
export const PROPOSAL_STATUS_LABEL: Record<ProposalStatus, string> = {
  accepted: "aceita",
  rejected: "recusada",
  expired: "expirada",
  countered: "contraproposta enviada",
};

export const SHARE_INVITATION_STATUS_LABEL: Record<ShareInvitationStatus, string> = {
  accepted: "aceito",
  rejected: "recusado",
  expired: "expirado",
};

export const PARTNER_INVITE_STATUS_LABEL: Record<PartnerInviteStatus, string> = {
  accepted: "aceitou",
  declined: "recusou",
};

export const ACCESS_AUTHORIZATION_RESPONSE_LABEL: Record<AccessAuthorizationResponse, string> = {
  authorized: "autorizado",
  denied: "recusado",
};

/** Frase curta (subtítulo do feed) para cada resposta do proprietário. */
export const AVAILABILITY_ANSWER_LABEL: Record<AvailabilityAnswer, string> = {
  available: "Continua disponível",
  closed_deal: "Fechou negócio (fora da plataforma)",
  paused: "Pausou por enquanto",
};

/** Decisão do proprietário sobre o destino do imóvel pós-encerramento (hub#760). */
export const RETURN_ANSWER_LABEL: Record<
  PropertyReturnRespondedPayload["answer"],
  string
> = {
  relist: "Quer anunciar de novo",
  repairs_first: "Vai fazer reparos antes",
  pause: "Parou por enquanto",
  withdraw: "Não vai seguir com você",
};

export const ACTIVITY_TYPE_LABEL: Record<ActivityType, string> = {
  visit: "Visita",
  call: "Ligação",
  meeting: "Reunião",
  follow_up: "Follow-up",
  inspection: "Vistoria",
  signing: "Assinatura",
  other: "Atividade",
};

export const LEAD_SOURCE_LABEL: Record<LeadSource, string> = {
  portal: "Portal",
  meta: "Meta Ads",
  whatsapp: "WhatsApp",
  manual: "Manual",
  referral: "Indicação",
};
